import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Tooltip,
} from '@fluentui/react-components'
import { AddFilled, AddRegular, ArrowDownloadFilled, ArrowDownloadRegular, bundleIcon } from '@fluentui/react-icons'
import { useDispatch } from 'react-redux'
import useModules from '../../hooks/module/useModules'
import useRoles, { Role } from '../../hooks/useRoles'
import { AppAction } from '../../config/appConfig'
import { MenuItemKey } from '../../components/MainMenu'
import { useTranslation } from 'react-i18next'
import { FileAction, FileStatus, IOverviewItemDto } from '../../config/fileConfig'
import useUser from '../../hooks/useUser'
import { useHistory } from 'react-router-dom'
import useViewport, { Viewport } from '../../hooks/useViewport'
import UserPicker from '../../components/TagPicker/UserPicker'
import FileStatusPicker, { FileStatusLabels } from '../../components/TagPicker/FileStatusPicker'
import { ModuleType } from '../../context/GlobalContext'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import useSubmitDocumentGenerationRequest from '../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationType } from '../../hooks/document/useDocumentGenerationHandler'
import { useSettings } from '../../hooks/useSettings'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, { BaseOverviewCount, BaseOverviewProps, parseFilterValuesToParams, useOverviewStyles } from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterType, SearchHintIcon } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import BaseHeaderItem from '../BaseOverview/BaseHeaderItem'
import { UserStatus } from '../../config/userConfig'
import { FileListItem } from './FileListItem'

export const useItemStyles = makeStyles({
    listFileItem: {
        display: 'grid',
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) auto var(--baseOverviewIconWidth) var(--baseOverviewFileWidth) var(--baseOverviewDateWidth)',
    },
    listFileItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewAvatarWidth) auto var(--baseOverviewDateWidth)',
    },
})

const AddIcon = bundleIcon(AddFilled, AddRegular)
const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)

export type FileFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        statuses?: FileStatus[]
        ownerUsers?: string[]
        executiveUsers?: string[]
        parties?: string[]
    }

type FileSorting = {
    id: string
    name: string
    reference: string
    'history.updated': string
}

export type FileSortingType = BaseOverviewSortingType<FileSorting>

export function FileFilter() {
    const { values, setFieldValue } = useFormikContext<FileFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    return (
        <>
            <Field label={translate('file.filter.owner')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.ownerUsers || []}
                    onOptionsSelect={(_, selectedOptions) => setFieldValue('ownerUsers', selectedOptions)}
                />
            </Field>
            <Field label={translate('file.filter.executive')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.executiveUsers || []}
                    onOptionsSelect={(_, selectedOptions) => setFieldValue('executiveUsers', selectedOptions)}
                />
            </Field>
            <Field label={translate('file.status.label')} orientation={orientation}>
                <FileStatusPicker
                    selectedOptions={values.statuses || []}
                    onOptionsSelect={(_, selectedOptions) => setFieldValue('statuses', selectedOptions)}
                />
            </Field>
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('file.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => setFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const headerSortingItems: FileSortingType[] = [
    {
        property: ['name', 'reference'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.az',
    },
    {
        property: ['name', 'reference'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.za',
    },
    {
        property: ['reference'],
        direction: SortDirection.Ascending,
    },
    {
        property: ['reference'],
        direction: SortDirection.Descending,
    },
    {
        property: ['history.updated', 'reference'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.lastaccessasc',
    },
    {
        property: ['history.updated', 'reference'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.lastaccessdesc',
        default: true,
    },
]

export function HeaderActionsButtons() {
    const history = useHistory()
    const roles = useRoles()
    const { t: translate } = useTranslation()
    const { values } = useFormikContext<FileFilterType>()

    const submitReportRequest = useSubmitDocumentGenerationRequest<FileFilterType>(DocumentGenerationType.FileXls)

    return (
        <>
            {roles.none([Role.FileLimited]) && (
                <Tooltip withArrow relationship={'label'} content={translate('action.create.file.title')}>
                    <Button icon={<AddIcon />} appearance={'primary'} onClick={() => history.push('/file/add')} />
                </Tooltip>
            )}
            {roles.all([Role.Report]) && (
                <Tooltip withArrow relationship={'label'} content={translate('action.download.selection')}>
                    <Button icon={<DownloadIcon />} appearance={'secondary'} onClick={() => submitReportRequest(parseFilterValuesToParams(values))} />
                </Tooltip>
            )}
        </>
    )
}

function HeaderFilterTags() {
    const { values, setValues } = useFormikContext<FileFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()

    return (
        <div className={overviewStyles.flexGapXS}>
            <InteractionTag
                selected={values.statuses?.includes(FileStatus.Open)}
                onClick={() =>
                    setValues({
                        ...values,
                        statuses: values.statuses?.includes(FileStatus.Open)
                            ? values.statuses.filter(value => value !== FileStatus.Open)
                            : [...values.statuses, FileStatus.Open],
                        tag: undefined,
                    })
                }
            >
                <InteractionTagPrimary>{translate(FileStatusLabels.get(FileStatus.Open).name)}</InteractionTagPrimary>
            </InteractionTag>
            <InteractionTag
                selected={values.statuses?.includes(FileStatus.Archived)}
                onClick={() =>
                    setValues({
                        ...values,
                        statuses: values.statuses?.includes(FileStatus.Archived)
                            ? values.statuses.filter(value => value !== FileStatus.Archived)
                            : [...values.statuses, FileStatus.Archived],
                        tag: undefined,
                    })
                }
            >
                <InteractionTagPrimary>{translate(FileStatusLabels.get(FileStatus.Archived).name)}</InteractionTagPrimary>
            </InteractionTag>
        </div>
    )
}

function FilterInputHint() {
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    return (
        <Popover open={open} onOpenChange={(e, data) => setOpen(data.open)} withArrow>
            <PopoverTrigger disableButtonEnhancement>
                <Button size={'small'} appearance={'transparent'} icon={<SearchHintIcon />} />
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
                <h3>{translate('file.search.hint.title')}</h3>
                <br />
                <div dangerouslySetInnerHTML={{ __html: translate('file.search.hint.body') }}></div>
            </PopoverSurface>
        </Popover>
    )
}

export default function FileOverview(props?: Partial<BaseOverviewProps<IOverviewItemDto, FileFilterType, FileSortingType>>) {
    const axios = useAxios()
    const dispatch = useDispatch()

    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { settings } = useSettings()
    const { currentUser } = useUser()

    //Reset breadcrumbs and data
    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Files] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [])

    const defaultFilterValues = useMemo<FileFilterType>(
        () => ({
            ownerUsers: currentUser?.status === UserStatus.Enabled ? [currentUser?.id] : [],
            executiveUsers: currentUser?.status === UserStatus.Enabled ? [currentUser?.id] : [],
            statuses: [FileStatus.Open],
            attributes: [],
            query: '',
        }),
        [settings],
    )

    const countItems = (column?: number, filterValues?: FileFilterType): Promise<BaseOverviewCount> => {
        return axios.get('/file/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        column?: number,
        filterValues?: FileFilterType,
        sortingValues?: FileSortingType,
        start?: number,
        size?: number,
    ): Promise<IOverviewItemDto[]> => {
        if ((sortingValues?.property?.[0] as string) === 'lastAccess') {
            sortingValues = { ...sortingValues, property: ['history.updated', 'id'] }
        }

        return axios
            .get('/file', { params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) } })
            .then((response: AxiosResponse<IOverviewItemDto[]>) => response?.data || [])
    }

    if (!settings) {
        return <></>
    }

    return (
        <BaseOverview<IOverviewItemDto, FileFilterType, FileSortingType>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={BaseHeaderItem}
            listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
            listItemContent={props => <FileListItem {...props} />}
            listItemClassnames={props => {
                return [itemStyles.listFileItem, props.viewportLarge && itemStyles.listFileItemSmall]
            }}
            headerDefaultFilterTags={HeaderFilterTags}
            headerActionsButtons={HeaderActionsButtons}
            headerSortingItems={headerSortingItems}
            filterContent={FileFilter}
            filterInputHint={FilterInputHint}
            inputPlaceholderKey={'file.filter.search'}
            localStorageKeyPrefix={'HammockFileOverview'}
            {...props}
        />
    )
}
